import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { DotsSixVertical, Trash, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const STAGES = ["Prospect identifié", "À contacter", "Premier échange", "NDA", "LOI", "Audit", "Négociation", "Closing", "Intégration"];

const SEQ_STYLE = {
  "Email initial": { bg: "#dbe7ff", fg: "#002FA7" },
  "Relance 1": { bg: "#fef3c7", fg: "#b45309" },
  "Relance 2": { bg: "#e2e8f0", fg: "#475569" },
  "Engagé": { bg: "#d1fae5", fg: "#047857" },
};
const HEAT_STYLE = {
  hot: { bg: "#fee2e2", fg: "#b91c1c", icon: "🔥" },
  warm: { bg: "#fef3c7", fg: "#b45309", icon: "🌤" },
  cold: { bg: "#f1f5f9", fg: "#64748b", icon: "❄️" },
};

export default function Pipeline() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [seq, setSeq] = useState({});
  const [dragId, setDragId] = useState(null);

  const load = () => api.get("/deals").then((r) => setDeals(r.data));
  useEffect(() => {
    load();
    api.get("/reports/pipeline-sequence").then((r) => setSeq(r.data || {})).catch(() => {});
  }, []);

  const move = async (deal, stage) => {
    if (deal.stage === stage) return;
    setDeals((ds) => ds.map((d) => (d.id === deal.id ? { ...d, stage } : d)));
    try { await api.patch(`/deals/${deal.id}`, { stage }); } catch { toast.error("Erreur"); load(); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/deals/${id}`);
    load();
  };

  return (
    <div>
      <header className="h-16 px-8 flex items-center justify-between border-b border-border bg-white sticky top-0 z-10">
        <h1 className="font-head font-bold text-xl">Pipeline M&A</h1>
        <div className="overline text-muted-foreground">{deals.length} deals · {eur(deals.reduce((s, d) => s + (d.valorisation || 0), 0))} valorisés</div>
      </header>

      <div className="p-6 overflow-x-auto kanban-scroll">
        <div className="flex gap-3 min-w-max pb-4">
          {STAGES.map((stage) => {
            const items = deals.filter((d) => d.stage === stage);
            return (
              <div key={stage} data-testid={`kanban-col-${stage}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { const d = deals.find((x) => x.id === dragId); if (d) move(d, stage); setDragId(null); }}
                className="w-64 shrink-0 bg-slate-50 border border-border flex flex-col">
                <div className="px-3 py-2.5 border-b border-border flex items-center justify-between bg-white">
                  <span className="text-sm font-semibold">{stage}</span>
                  <span className="font-mono text-xs bg-secondary px-1.5 py-0.5">{items.length}</span>
                </div>
                <div className="p-2 space-y-2 min-h-[120px] flex-1">
                  {items.map((d) => (
                    <div key={d.id} draggable onDragStart={() => setDragId(d.id)}
                      onClick={() => navigate(`/cabinet/${d.company_id}`)} data-testid={`deal-card-${d.id}`}
                      className="bg-white border border-border p-3 cursor-pointer hover:border-primary group">
                      <div className="flex items-start gap-1">
                        <DotsSixVertical size={14} className="text-muted-foreground mt-0.5 cursor-grab shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate">{d.company_nom}</div>
                          <div className="text-xs text-muted-foreground">{d.ville}</div>
                        </div>
                        <button onClick={(e) => remove(d.id, e)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"><Trash size={14} /></button>
                      </div>
                      {seq[d.company_id] && (seq[d.company_id].in_sequence || seq[d.company_id].heat?.score > 0) && (
                        <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`seq-badge-${d.id}`}>
                          {seq[d.company_id].in_sequence && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                              style={{ background: (SEQ_STYLE[seq[d.company_id].label] || SEQ_STYLE["Relance 2"]).bg, color: (SEQ_STYLE[seq[d.company_id].label] || SEQ_STYLE["Relance 2"]).fg }}>
                              <EnvelopeSimple size={10} weight="fill" /> {seq[d.company_id].label}
                            </span>
                          )}
                          {seq[d.company_id].heat && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" data-testid={`heat-badge-${d.id}`}
                              style={{ background: HEAT_STYLE[seq[d.company_id].heat.level].bg, color: HEAT_STYLE[seq[d.company_id].heat.level].fg }}
                              title={`Score de chaleur : ${seq[d.company_id].heat.score}/100`}>
                              {HEAT_STYLE[seq[d.company_id].heat.level].icon} {seq[d.company_id].heat.label} {seq[d.company_id].heat.score}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex justify-between mt-2 pt-2 border-t border-border">
                        <span className="font-mono text-xs text-muted-foreground">{eur(d.commissions)}</span>
                        <span className="font-mono text-xs font-semibold text-primary">{eur(d.valorisation)}</span>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">—</div>}
                </div>
              </div>
            );
          })}
        </div>
        {deals.length === 0 && <p className="text-sm text-muted-foreground mt-4">Ajoutez des cabinets au pipeline depuis le Deal Sourcing.</p>}
      </div>
    </div>
  );
}
